import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { catchError, from, Observable, switchMap, tap, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { AuthUtils } from './auth.utils';
import { getAccessToken, RequestCollectorService, SKIP_COLLECTION_HEADER, UNAUTHORIZED_REQUEST_HEADER } from 'projects/lib-shared-common/src/public-api';
import { AuthCheckService } from './auth-check.service';
import { Router } from '@angular/router';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  /**
   * Constructor
   */
  constructor(
    private _authService: AuthService,
    private _router: Router,
    private _requestCollectorService: RequestCollectorService,
    private _authCheckService: AuthCheckService
  ) {}

  /**
   * Intercept
   *
   * @param req
   * @param next
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return new Observable((subscriber) => {
      const isAuthorizedRequest = !req.headers.has(UNAUTHORIZED_REQUEST_HEADER);

      if (isAuthorizedRequest && this._authCheckService.checkingIsActive && !this._authCheckService.sessionStateIsValid()) {
        subscriber.complete();
        return () => {};
      }

      let collectRequest = true;
      let newReq = req.clone();

      if (newReq.headers.has(SKIP_COLLECTION_HEADER)) {
        collectRequest = false;
        newReq = newReq.clone({
          headers: newReq.headers.delete(SKIP_COLLECTION_HEADER),
        });
      }

      const isStaticAssetRequest = req.method === 'GET' && req.url.includes('assets/icons/');

      if (isAuthorizedRequest && !isStaticAssetRequest && !newReq.headers.get('Authorization')) {
        try {
          const token = this._authService.accessToken || getAccessToken();
          if (token && !AuthUtils.isTokenExpired(token)) {
            newReq = newReq.clone({
              headers: newReq.headers.set('Authorization', 'Bearer ' + token),
            });
          }
        } catch (e) {
          console.error(e);
        }
      } else if (!isStaticAssetRequest) {
        newReq = newReq.clone({
          headers: newReq.headers.delete(UNAUTHORIZED_REQUEST_HEADER),
        });
      }

      // Response
      let subscription = next
        .handle(newReq)
        .pipe(
          catchError((error): Observable<HttpEvent<any>> => {
            if (error instanceof HttpErrorResponse && error.status === 401) {
              if (this._authService.isEmbeddedModule) {
                const callbackNames = ['angularExternalFeaturesUnauthorized', 'angularNotificationUnauthorized'];
                callbackNames.forEach((name) => {
                  const target = (parent ?? top)?.window?.[name];
                  if (typeof target === 'function') {
                    target();
                  }
                });
                return throwError(() => error);
              }
              if (!this._authService.isFeedModule) {
                this._router.navigate(['/sign-out']);
                return throwError(() => error);
              }
              return from(this._authService.refreshApiToken()).pipe(
                switchMap((token) => {
                  return next.handle(
                    newReq.clone({
                      headers: newReq.headers.set('Authorization', 'Bearer ' + token),
                    })
                  );
                }),
                catchError((err) => {
                  if (err instanceof HttpErrorResponse && err.status === 401) this._router.navigate(['/sign-out']);
                  return throwError(() => err);
                })
              );
            }
            return throwError(() => error);
          }),
          tap({
            next: (res) => subscriber.next(res),
            complete: () => {
              // defer session-check call to avoid triggering change-detection during current cycle
              Promise.resolve().then(() => this._authCheckService.interceptRequest());
              subscriber.complete();
            },
            finalize: () => subscriber.complete(),
            error: (error) => subscriber.error(error),
          })
        )
        .subscribe();

      if (collectRequest) this._requestCollectorService.add(subscription, newReq);

      return () => subscription.unsubscribe();
    });
  }
}
